import {
  SocketMessageInterface,
  HallCallsState,
  DoorState,
  NO_ACK_VERDICT,
  AckVerdict,
  OverlaidMark,
  WithheldMark,
} from "../../interfaces/Monitoring.interface";
import PositionCallsStatusT from "../../types/PositionCallsStatus";
import { DeviceDirection } from "../../types/DeviceDirection";
import {
  UsageMap,
  DIRECTION_USAGE_CODES,
  ELEVATOR_IDLE_USAGE_CODES,
} from "../../types/monitoringTypes";
import { Usage } from "../generated/usage/usage";
import { Traffic } from "../generated/traffic/traffic";
import { Control } from "../generated/control/control";
import { Status } from "../generated/status/status";
import { Fault } from "../generated/fault/fault";
import { Service } from "../generated/service/service";
import { FaultMessageT } from "../generated/fault/fault-message";
import { ElevatorFaultMessageT } from "../generated/fault/elevator-fault-message";
import { EscalatorFaultMessageT } from "../generated/fault/escalator-fault-message";
import { FaultContent } from "../generated/fault/fault-content";
import { ServiceMessageT } from "../generated/service/service-message";
import { ElevatorServiceMessageT } from "../generated/service/elevator-service-message";
import { EscalatorServiceMessageT } from "../generated/service/escalator-service-message";
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { EscalatorTrafficMessageT } from "../generated/traffic/escalator-traffic-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { ElevatorUsageMessageT } from "../generated/usage/elevator-usage-message";
import { EscalatorUsageMessageT } from "../generated/usage/escalator-usage-message";

import { FrontEndMessageT } from "../generated/front-end/front-end-message";
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";
import { TrafficMessageT } from "../generated/traffic/traffic-message";
import { ControlMessageT } from "../generated/control/control-message";
import { UsageMessageT } from "../generated/usage/usage-message";
import { StatusMessageT } from "../generated/status/status-message";
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { DeviceUsageContent } from "../generated/usage/device-usage-content";
import { ElevatorStatusMessageT } from "../generated/status/elevator-status-message";
import { EscalatorStatusMessageT } from "../generated/status/escalator-status-message";
import { DeviceStatusContent } from "../generated/status/device-status-content";

import { UuidT } from "../generated/front-end/uuid";
import {
  bankKey,
  compoundKey,
  bitmaskPrefix,
  faultNumericKey,
  oneHotKey,
} from "../../utils/typedKeys";
import { OneHotEntry } from "../../utils/stateMapHelpers";
import { connectivityFromStatus } from "./connectionStatus";
import { extractConnectionStatus } from "./frontEndMessageDecoder";
import {
  buildDefaultExclusionGroups,
  ExclusionKey,
  ExclusionRule,
} from "../../utils/buildExclusionMap";
import { tracePointEmit } from "../../integrationTrace";
import { TracePointId } from "../generated/integration-trace/trace-point-id";
import type { HallCallBinding } from "../../interfaces/IconDefinitions";
import { AckOutcome } from "../generated/front-end/ack-outcome";
import { AckVerified } from "../generated/front-end/ack-verified";
import { clearWithheldMarksAt, frameTimeMs, pruneWithheldMarks } from "./withheldMarks";
import { updateOverlaidMarks } from "./overlaidMarks";
import { isSequenceProgress } from "./sequenceProgress";
import { devWarn } from "../../utils/devLog";

// ---------------------------------------------------------
// Development-only logging for unknown enum values.
// Each unique (enumName, code) pair is logged at most once.
// ---------------------------------------------------------
const loggedUnknownEnums = new Set<string>();

function warnUnknownEnum(enumName: string, code: number, enumObj: Record<number, string>): void {
  if (process.env.NODE_ENV !== "development") return;
  if (enumObj[code] !== undefined) return;
  const logKey = `${enumName}:${code}`;
  if (loggedUnknownEnums.has(logKey)) return;
  loggedUnknownEnums.add(logKey);
  devWarn(`[Monitoring] Unknown ${enumName} code: ${code}`);
}

// ---------------------------------------------------------
// Default empty state for a new lift
// ---------------------------------------------------------
/**
 * A zeroed lift state for a bank/lift pair, used before the first frame arrives.
 */
export function createDefaultState(bankId: UuidT, liftId: UuidT): SocketMessageInterface {
  return {
    bankId,
    liftId,
    carCalls: new PositionCallsStatusT(),
    hallCalls: {
      up: new PositionCallsStatusT(),
      down: new PositionCallsStatusT(),
      upAssigned: new PositionCallsStatusT(),
      downAssigned: new PositionCallsStatusT(),
    },
    faults: false,
    activeFaults: [],
    position: null,
    serviceMode: null,
    direction: null,
    doorStatus: {
      frontOpen: false,
      rearOpen: false,
      frontStalled: false,
      rearStalled: false,
    },
    doorZone: null,
    levelling: false,
    escalatorControl: null,
    // Disconnected until the controller says otherwise. Connectivity is
    // reported for the BANK, and a bank that has not yet reported is not known
    // to be reachable — defaulting to true asserted something no message had
    // said, and meant a lift could silently look live while nothing was
    // arriving for it.
    connected: false,
    msg: {
      contentType: EncodedMessageContent.BitMaskMessage,
      messageType: MessageContent.Status_StatusMessage,
      content: null,
      active: false,
    },
    usage: {},
    bitmask: {},
    positionMap: {},
    serviceMap: {},
    oneHotMap: {},
    // Not an acknowledgement until a verdict says otherwise.
    ack: NO_ACK_VERDICT,
    // Nothing has been withheld on a lift nothing has been heard from.
    withheldCalls: [],
    // No floor is known to be secured, and no other bank has drawn anything here.
    secured: { front: [], rear: [] },
    overlaid: [],
  };
}

// ---------------------------------------------------------
// Helpers for position arrays (add/remove floor numbers)
// ---------------------------------------------------------
function addToArray(arr: number[], position: number): number[] {
  if (arr.includes(position)) return arr;
  return [...arr, position].sort((a, b) => a - b);
}

function removeFromArray(arr: number[], position: number): number[] {
  if (!arr.includes(position)) return arr;
  return arr.filter((p) => p !== position);
}

function updatePositionArray(arr: number[], position: number, active: boolean): number[] {
  return active ? addToArray(arr, position) : removeFromArray(arr, position);
}

// ---------------------------------------------------------
// Deep clone helpers for immutable updates
// ---------------------------------------------------------
function clonePositionCalls(pc: PositionCallsStatusT): PositionCallsStatusT {
  return new PositionCallsStatusT([...pc.front], [...pc.rear], [...pc.frontLock], [...pc.rearLock]);
}

function cloneHallCallsState(hc: HallCallsState): HallCallsState {
  return {
    up: clonePositionCalls(hc.up),
    down: clonePositionCalls(hc.down),
    upAssigned: clonePositionCalls(hc.upAssigned),
    downAssigned: clonePositionCalls(hc.downAssigned),
  };
}

// ---------------------------------------------------------
// Hall-call bindings
// ---------------------------------------------------------

/**
 * Apply any adapter-configured hall-call binding that matches this message.
 *
 * Returns the state unchanged when there are no bindings, none match, or the
 * message carries no floor — so an adapter that has configured nothing behaves
 * byte-for-byte as it did before bindings existed.
 *
 * Why this exists: only the `Traffic::*HallCall` family feeds `state.hallCalls`,
 * so a controller that reports its hall calls some other way never lights a
 * dot. On a Swift CEC the up hall call arrives as
 * `Control::EnergizeUpHallCall`; `applyElevatorControlDelta` already receives
 * it with the floor in `position`, and drops it on the floor because the switch
 * has no arm for it.
 *
 * Active follows the message's own flag and the floor comes from its position,
 * which is why one binding covers every floor. The write is idempotent —
 * `updatePositionArray` adds or removes a single floor — so a controller that
 * reports the same call through both a Traffic message and a bound one lands on
 * the same array entry rather than double-counting.
 */
function applyHallCallBindings(
  state: SocketMessageInterface,
  contentType: EncodedMessageContent,
  messageType: MessageContent,
  message: number,
  position: number | null | undefined,
  active: boolean,
  bindings?: ReadonlyArray<HallCallBinding>,
): SocketMessageInterface {
  if (!bindings || bindings.length === 0) return state;
  if (position == null) return state;

  const matched = bindings.filter(
    (b) => b.contentType === contentType && b.messageType === messageType && b.message === message,
  );
  if (matched.length === 0) return state;

  const hallCalls = cloneHallCallsState(state.hallCalls);
  matched.forEach((b) => {
    switch (b.slot) {
      case "upFront":
        hallCalls.up.front = updatePositionArray(hallCalls.up.front, position, active);
        break;
      case "upRear":
        hallCalls.up.rear = updatePositionArray(hallCalls.up.rear, position, active);
        break;
      case "downFront":
        hallCalls.down.front = updatePositionArray(hallCalls.down.front, position, active);
        break;
      case "downRear":
        hallCalls.down.rear = updatePositionArray(hallCalls.down.rear, position, active);
        break;
      default:
        break;
    }
  });

  return { ...state, hallCalls };
}

// ---------------------------------------------------------
// Helper: extract inner enum code from variant union
// ---------------------------------------------------------
function variantMessage(variant: any): number {
  if (variant && typeof variant.message === "number") {
    return variant.message;
  }
  if (process.env.NODE_ENV === "development" && variant != null) {
    devWarn(
      `[VARIANT] FAILED to extract .message from variant:`,
      variant.constructor?.name ?? typeof variant,
      "keys=",
      Object.keys(variant),
      variant,
    );
  }
  // Return -1 so no switch/if branch matches a real enum value.
  // Returning 0 would collide with Traffic.P, Usage.Up, Control.CarCallLock.
  return -1;
}

// ---------------------------------------------------------
// Apply a traffic delta — switch on Traffic enum
// ---------------------------------------------------------
function applyElevatorTrafficDelta(
  state: SocketMessageInterface,
  update: ElevatorTrafficMessageT,
  active: boolean,
): SocketMessageInterface {
  const code = variantMessage(update.traffic);
  warnUnknownEnum("Traffic", code, Traffic);
  const position = update.position;

  // Position update is a direct set, not add/remove.
  //
  // AN INACTIVE P NAMES NO FLOOR THE CAR IS AT. `active` on P says the car is
  // at `position`; its de-assert says only that the car is no longer at that
  // floor. Applying it as a set ("last one wins") moved the car to a floor it
  // had just left — an adapter that de-asserts the old floor after asserting
  // the new one parked the car on the old floor until the next move. It does
  // not clear the position either, even when it names the current floor: the
  // car has not vanished from the hoistway, and a null position hides it from
  // the grid and raises "Awaiting: position" for a car that is merely between
  // position reports. This is the same contract the door-close usage codes
  // follow below: a de-assert leaves the steady state untouched. The call
  // arrays differ only because they are sets — removing the named floor from a
  // set is exactly what a de-assert means there.
  if (code === Traffic.P) {
    if (!active) return state;
    const posKey = compoundKey(state.bankId, state.liftId);
    if (state.position === position && state.positionMap[posKey] === position) return state;
    return {
      ...state,
      position,
      positionMap: { ...state.positionMap, [posKey]: position },
    };
  }

  // carCalls fields — only clone carCalls.
  // The *Answered variants are acknowledgements, not call state: controllers
  // (MCE2000) send CarCall:true, then CarCall:false once attended, and can
  // follow with an Answered:true that is never de-asserted. Driving the icon
  // from Answered left attended calls lit indefinitely (LIFT-1223), so only
  // the plain call messages drive carCalls.
  if (code === Traffic.CarCallAnswered || code === Traffic.RearCarCallAnswered) return state;
  if (code === Traffic.CarCall || code === Traffic.RearCarCall) {
    const newCarCalls = clonePositionCalls(state.carCalls);
    if (code === Traffic.CarCall) {
      newCarCalls.front = updatePositionArray(newCarCalls.front, position, active);
    } else {
      newCarCalls.rear = updatePositionArray(newCarCalls.rear, position, active);
    }
    return { ...state, carCalls: newCarCalls };
  }

  // hallCalls fields — only clone hallCalls.
  // HallCallAnswered variants are acknowledgements, not call state, and never
  // drive the icon (see the car-call note above, LIFT-1223).
  // *HallCallAssigned variants populate the upAssigned/downAssigned arrays so
  // FloorCellIcon renders them as green (up) / red (down) call dots alongside
  // car calls — these participate in the dynamic sizing tier (callDotCount /
  // activeCount) automatically since FloorCellIcon already reads
  // hallCalls.upAssigned/downAssigned.
  const newHallCalls = cloneHallCallsState(state.hallCalls);
  switch (code) {
    case Traffic.HallCall:
    case Traffic.UpHallCall:
      newHallCalls.up.front = updatePositionArray(newHallCalls.up.front, position, active);
      break;
    case Traffic.UpRearHallCall:
      newHallCalls.up.rear = updatePositionArray(newHallCalls.up.rear, position, active);
      break;
    case Traffic.DownHallCall:
      newHallCalls.down.front = updatePositionArray(newHallCalls.down.front, position, active);
      break;
    case Traffic.DownRearHallCall:
      newHallCalls.down.rear = updatePositionArray(newHallCalls.down.rear, position, active);
      break;
    case Traffic.HallCallAnswered:
    case Traffic.UpHallCallAnswered:
    case Traffic.UpRearHallCallAnswered:
    case Traffic.DownHallCallAnswered:
    case Traffic.DownRearHallCallAnswered:
      return state;
    case Traffic.UpHallCallAssigned:
      newHallCalls.upAssigned.front = updatePositionArray(
        newHallCalls.upAssigned.front,
        position,
        active,
      );
      break;
    case Traffic.UpRearHallCallAssigned:
      newHallCalls.upAssigned.rear = updatePositionArray(
        newHallCalls.upAssigned.rear,
        position,
        active,
      );
      break;
    case Traffic.DownHallCallAssigned:
      newHallCalls.downAssigned.front = updatePositionArray(
        newHallCalls.downAssigned.front,
        position,
        active,
      );
      break;
    case Traffic.DownRearHallCallAssigned:
      newHallCalls.downAssigned.rear = updatePositionArray(
        newHallCalls.downAssigned.rear,
        position,
        active,
      );
      break;
    default:
      return state;
  }

  return { ...state, hallCalls: newHallCalls };
}

// ---------------------------------------------------------
// Apply a control delta — switch on Control enum
// ---------------------------------------------------------
function applyElevatorControlDelta(
  state: SocketMessageInterface,
  update: ElevatorControlMessageT,
  active: boolean,
): SocketMessageInterface {
  const code = variantMessage(update.control);
  warnUnknownEnum("Control", code, Control);
  const position = update.position;

  // A secured floor: SecureCarCall (front) and SecureRearCarCall (rear), as a
  // controller, a relay module or a discrete input reports them. Kept apart
  // from the car-call locks below: both draw a padlock, but only a lock
  // disables the floor's normal car-call button, and a secured floor's call is
  // the one an interlock or the controller is there to decide on.
  if (code === Control.SecureCarCall || code === Control.SecureRearCarCall) {
    const secured =
      code === Control.SecureCarCall
        ? { ...state.secured, front: updatePositionArray(state.secured.front, position, active) }
        : { ...state.secured, rear: updatePositionArray(state.secured.rear, position, active) };
    return { ...state, secured };
  }

  // carCalls lock fields — only clone carCalls
  if (
    code === Control.CarCallLock ||
    code === Control.CarCallUnlock ||
    code === Control.RearCarCallLock ||
    code === Control.RearCarCallUnlock
  ) {
    const newCarCalls = clonePositionCalls(state.carCalls);
    if (code === Control.CarCallLock || code === Control.CarCallUnlock) {
      newCarCalls.frontLock = updatePositionArray(newCarCalls.frontLock, position, active);
    } else {
      newCarCalls.rearLock = updatePositionArray(newCarCalls.rearLock, position, active);
    }
    return { ...state, carCalls: newCarCalls };
  }

  // hallCalls fields — only clone hallCalls
  const newHallCalls = cloneHallCallsState(state.hallCalls);
  switch (code) {
    case Control.UpHallCallLock:
    case Control.UpHallCallUnlock:
      newHallCalls.up.frontLock = updatePositionArray(newHallCalls.up.frontLock, position, active);
      break;
    case Control.UpRearHallCallLock:
    case Control.UpRearHallCallUnlock:
      newHallCalls.up.rearLock = updatePositionArray(newHallCalls.up.rearLock, position, active);
      break;
    case Control.DownHallCallLock:
    case Control.DownHallCallUnlock:
      newHallCalls.down.frontLock = updatePositionArray(
        newHallCalls.down.frontLock,
        position,
        active,
      );
      break;
    case Control.DownRearHallCallLock:
    case Control.DownRearHallCallUnlock:
      newHallCalls.down.rearLock = updatePositionArray(
        newHallCalls.down.rearLock,
        position,
        active,
      );
      break;
    default:
      return state;
  }
  // NOTE: assigned hall calls (Up/Down [Rear] Assigned Hall Call) used to
  // be Control enum members and were handled here, but the FlatBuffer
  // schema removed them from Control (they live on Usage now). The
  // canonical channel for that state today is Traffic.{Up,Down}[Rear]
  // HallCallAssigned, which applyElevatorTrafficDelta already routes
  // into hallCalls.upAssigned / hallCalls.downAssigned.

  return { ...state, hallCalls: newHallCalls };
}

// ---------------------------------------------------------
// Apply an escalator control delta
// ---------------------------------------------------------
function applyEscalatorControlDelta(
  state: SocketMessageInterface,
  update: EscalatorControlMessageT,
  active: boolean,
): SocketMessageInterface {
  warnUnknownEnum("Control", variantMessage(update.control), Control);
  const newValue: Control | null = active ? variantMessage(update.control) : null;
  if (state.escalatorControl === newValue) return state;
  return { ...state, escalatorControl: newValue };
}

// ---------------------------------------------------------
// Direction code sets
// ---------------------------------------------------------
// Shared with usageHasDirectionData so "which codes are direction data" and
// "which codes this decoder resolves to a direction" cannot drift apart.
const ELEVATOR_DIRECTION_CODES: ReadonlySet<Usage> = new Set(DIRECTION_USAGE_CODES);

const ELEVATOR_IDLE_CODES: ReadonlySet<Usage> = new Set(ELEVATOR_IDLE_USAGE_CODES);

const ESCALATOR_IDLE_CODES = new Set([Usage.FaultStopped, Usage.Stopped, Usage.Disconnected]);

// The escalator Usage codes that say which way the steps run, and the direction each sets.
// RunUp/RunDown are the running form of Up/Down. Every other active code (Moving,
// InspectionServiceUsage, MechanicOnsiteUsage, ...) says nothing about the direction and leaves
// it as it was: taking any code as the direction showed a running escalator as stopped whenever
// such a code arrived after Up or Down (LN1 `Run` maps to Moving on every KONE escalator).
const ESCALATOR_DIRECTION_CODES: ReadonlyMap<Usage, Usage> = new Map([
  [Usage.Up, Usage.Up],
  [Usage.Down, Usage.Down],
  [Usage.RunUp, Usage.Up],
  [Usage.RunDown, Usage.Down],
  [Usage.Forward, Usage.Forward],
  [Usage.Backward, Usage.Backward],
]);

// Map each "Assigned hall call" Usage variant to the hallCalls.* slot
// it should toggle. Used by applyElevatorUsageDelta to populate the
// hoistway dots from steady-state Usage messages (replacing the
// removed Control.UpAssignedHallCall / DownAssignedHallCall handlers).
type AssignedHallCallTarget = "upFront" | "upRear" | "downFront" | "downRear";

const ELEVATOR_ASSIGNED_HALL_CALL_TARGETS = new Map<number, AssignedHallCallTarget>([
  [Usage.UpAssignedHallCall, "upFront"],
  [Usage.UpAssignedRearHallCall, "upRear"],
  [Usage.DownAssignedHallCall, "downFront"],
  [Usage.DownAssignedRearHallCall, "downRear"],
]);

// extractValuedUsagePosition pulls the floor number off a ValuedUsage
// payload. The Value union has many variants (Bool, Byte, Short, Int,
// Long, Float, Double, String + their unsigned siblings); the numeric
// ones all expose `.value: number | bigint` on the generated *T type.
// Returns 0 for NormalUsage or non-numeric values — the caller treats
// position=0 as "ground floor / unknown", which matches the prior
// Control-based behaviour when the wire payload was missing.
function extractValuedUsagePosition(variant: unknown): number {
  if (!variant || typeof variant !== "object") return 0;

  const value = (variant as { value?: unknown }).value;
  if (!value || typeof value !== "object") return 0;

  const inner = (value as { value?: unknown }).value;
  if (typeof inner === "number") return inner;
  if (typeof inner === "bigint") return Number(inner);

  return 0;
}

// ---------------------------------------------------------
// Apply a usage delta — switch on Usage enum
// ---------------------------------------------------------

// The six (five here — the schema predates the Invalid codes) door usage
// codes of one side are a one-hot *level*: the door is in exactly one of
// them. They must never be co-asserted in the usage map.
const FRONT_DOOR_USAGE_CODES = [
  Usage.FrontDoorOpening,
  Usage.FrontDoorClosing,
  Usage.FrontDoorOpened,
  Usage.FrontDoorClosed,
  Usage.FrontDoorStalled,
] as const;
const REAR_DOOR_USAGE_CODES = [
  Usage.RearDoorOpening,
  Usage.RearDoorClosing,
  Usage.RearDoorOpened,
  Usage.RearDoorClosed,
  Usage.RearDoorStalled,
] as const;

const DOOR_USAGE_SIDES: ReadonlyMap<Usage, readonly Usage[]> = new Map<Usage, readonly Usage[]>([
  ...FRONT_DOOR_USAGE_CODES.map(
    (code) => [code, FRONT_DOOR_USAGE_CODES] as [Usage, readonly Usage[]],
  ),
  ...REAR_DOOR_USAGE_CODES.map(
    (code) => [code, REAR_DOOR_USAGE_CODES] as [Usage, readonly Usage[]],
  ),
]);

function updateUsageMap(state: SocketMessageInterface, code: Usage, active: boolean): UsageMap {
  if (state.usage[code] === active) return state.usage;
  const usage: UsageMap = { ...state.usage, [code]: active };

  // A door status is a level, not an event: asserting one door code means
  // the door left every other state of its side. Clear the siblings so a
  // stale assert from an older telemetry source cannot latch — the icon
  // layer reads any latched transition bit as "doors opening" and paints it
  // over the steady open/closed pair (LIFT-632: cars traveling with the
  // door icon stuck open). A de-assert clears nothing: the one being
  // cleared is `code` itself, and the door's other states are simply
  // unreported again.
  if (active) {
    (DOOR_USAGE_SIDES.get(code) ?? []).forEach((sibling) => {
      if (sibling !== code) usage[sibling] = false;
    });
  }

  return usage;
}

function applyElevatorUsageDelta(
  state: SocketMessageInterface,
  update: ElevatorUsageMessageT,
  active: boolean,
): SocketMessageInterface {
  const code: Usage = variantMessage(update.usage);
  warnUnknownEnum("Usage", code, Usage);

  const updatedUsage = updateUsageMap(state, code, active);

  // Direction codes
  if (ELEVATOR_DIRECTION_CODES.has(code) || ELEVATOR_IDLE_CODES.has(code)) {
    const directionValue: DeviceDirection = active && !ELEVATOR_IDLE_CODES.has(code) ? code : null;
    return { ...state, usage: updatedUsage, direction: directionValue };
  }

  // Front door open
  if (code === Usage.FrontDoorOpened || code === Usage.FrontDoorOpening) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      frontOpen: active,
    };
    return { ...state, usage: updatedUsage, doorStatus: newDoor };
  }

  // Front door close
  //
  // A DE-ASSERT SAYS NOTHING ABOUT OPEN. `frontOpen: !active` read a
  // `FrontDoorClosed = false` as "the door is open", but the close signal
  // ceasing only means it is no longer asserting closed — the door could
  // equally be mid-travel, or simply unreported. Inventing "open" there is how
  // a car whose front door had merely stopped claiming closed came to read
  // "Front Door: Open" on the State tab.
  //
  // Worse, it made two consumers disagree from the identical event: the icon
  // layer's resolveSteadyDoorState reads the same Opened=false/Closed=false
  // pair as genuine ambiguity and renders a warning triangle, while this wrote
  // a confident "Open" beside it. Leaving the steady state untouched on a
  // de-assert keeps both honest — the usage map above still records the event,
  // so the icon layer retains the evidence it needs to say "unknown".
  if (code === Usage.FrontDoorClosed || code === Usage.FrontDoorClosing) {
    if (!active) return { ...state, usage: updatedUsage };

    return { ...state, usage: updatedUsage, doorStatus: { ...state.doorStatus, frontOpen: false } };
  }

  // Rear door open
  if (code === Usage.RearDoorOpened || code === Usage.RearDoorOpening) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      rearOpen: active,
    };
    return { ...state, usage: updatedUsage, doorStatus: newDoor };
  }

  // Rear door close — same contract as the front. See above.
  if (code === Usage.RearDoorClosed || code === Usage.RearDoorClosing) {
    if (!active) return { ...state, usage: updatedUsage };

    return { ...state, usage: updatedUsage, doorStatus: { ...state.doorStatus, rearOpen: false } };
  }

  // Door zone
  if (code === Usage.AboveDoorZone || code === Usage.InDoorZone || code === Usage.BelowDoorZone) {
    const newZone = active ? code : null;
    if (state.doorZone === newZone && updatedUsage === state.usage) return state;
    return { ...state, usage: updatedUsage, doorZone: newZone };
  }

  // Door stalled
  if (code === Usage.FrontDoorStalled) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      frontStalled: active,
    };
    return { ...state, usage: updatedUsage, doorStatus: newDoor };
  }
  if (code === Usage.RearDoorStalled) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      rearStalled: active,
    };
    return { ...state, usage: updatedUsage, doorStatus: newDoor };
  }

  // Levelling
  if (code === Usage.Levelling) {
    if (state.levelling === active && updatedUsage === state.usage) return state;
    return { ...state, usage: updatedUsage, levelling: active };
  }

  // Assigned hall calls — populate hallCalls.{up,down}Assigned for the
  // hoistway dot. These four Usage variants replace the equivalent
  // Control enum members that the FlatBuffer schema removed; Usage is
  // now the steady-state source of truth for "this floor still has an
  // assigned hall call" (Traffic.*HallCallAssigned remains as the
  // transition signal). ElevatorUsageMessage has no top-level
  // position field, so the floor number rides on the ValuedUsage
  // payload as a numeric Value variant (Int/Short/Byte/etc).
  const assignedTarget = ELEVATOR_ASSIGNED_HALL_CALL_TARGETS.get(code);
  if (assignedTarget) {
    const position = extractValuedUsagePosition(update.usage);
    const newHallCalls = cloneHallCallsState(state.hallCalls);

    switch (assignedTarget) {
      case "upFront":
        newHallCalls.upAssigned.front = updatePositionArray(
          newHallCalls.upAssigned.front,
          position,
          active,
        );
        break;
      case "upRear":
        newHallCalls.upAssigned.rear = updatePositionArray(
          newHallCalls.upAssigned.rear,
          position,
          active,
        );
        break;
      case "downFront":
        newHallCalls.downAssigned.front = updatePositionArray(
          newHallCalls.downAssigned.front,
          position,
          active,
        );
        break;
      case "downRear":
        newHallCalls.downAssigned.rear = updatePositionArray(
          newHallCalls.downAssigned.rear,
          position,
          active,
        );
        break;
      default:
        break;
    }

    return { ...state, usage: updatedUsage, hallCalls: newHallCalls };
  }

  // Other usage codes — still update the usage map
  if (updatedUsage !== state.usage) {
    return { ...state, usage: updatedUsage };
  }

  return state;
}

// ---------------------------------------------------------
// Apply an escalator usage delta
// ---------------------------------------------------------
function applyEscalatorUsageDelta(
  state: SocketMessageInterface,
  update: EscalatorUsageMessageT,
  active: boolean,
): SocketMessageInterface {
  const code: Usage = variantMessage(update.usage);
  warnUnknownEnum("Usage", code, Usage);
  const updatedUsage = updateUsageMap(state, code, active);
  if (!active) {
    if (updatedUsage !== state.usage) {
      return { ...state, usage: updatedUsage };
    }
    return state;
  }
  const directionValue: DeviceDirection = ESCALATOR_IDLE_CODES.has(code)
    ? null
    : (ESCALATOR_DIRECTION_CODES.get(code) ?? state.direction);
  if (state.direction === directionValue && updatedUsage === state.usage) return state;
  return { ...state, usage: updatedUsage, direction: directionValue };
}

// ---------------------------------------------------------
// Apply a fault delta
// ---------------------------------------------------------
function applyFaultDelta(
  state: SocketMessageInterface,
  update: FaultMessageT,
  active: boolean,
): SocketMessageInterface {
  const innerFault = update.fault as ElevatorFaultMessageT | EscalatorFaultMessageT | null;
  warnUnknownEnum("Fault", variantMessage(innerFault?.fault), Fault);
  const fk = faultNumericKey(update.faultType, variantMessage(innerFault?.fault));
  const faultMap = new Map(
    state.activeFaults.map((f) => {
      const inner = f.fault as ElevatorFaultMessageT | EscalatorFaultMessageT | null;
      return [faultNumericKey(f.faultType, variantMessage(inner?.fault)), f];
    }),
  );

  if (active) {
    if (faultMap.has(fk)) return state;
    faultMap.set(fk, update);
  } else {
    if (!faultMap.has(fk)) return state;
    faultMap.delete(fk);
  }

  return {
    ...state,
    activeFaults: Array.from(faultMap.values()),
  };
}

// ---------------------------------------------------------
// Apply a service delta
// ---------------------------------------------------------
function applyServiceDelta(
  state: SocketMessageInterface,
  update: ServiceMessageT,
  active: boolean,
  serviceName: string,
): SocketMessageInterface {
  const newMode = active ? update : null;
  const innerMsg = update.message as ElevatorServiceMessageT | ServiceMessageT | null;
  const currentInnerMsg = state.serviceMode?.message as
    ElevatorServiceMessageT | ServiceMessageT | null;

  const incomingMatchesStored =
    state.serviceMode?.messageType === update.messageType &&
    variantMessage(currentInnerMsg?.message) === variantMessage(innerMsg?.message);

  // An `active=false` service message only clears the stored mode when the
  // incoming message matches the currently-stored mode. A deactivation for an
  // unrelated peer (e.g. a stale `Capture(active=false)` arriving after
  // `Oos(active=true)` is already the live state) must NOT erase the live
  // service mode — that would re-introduce the order-dependent rendering bug
  // upstream snapshots could otherwise inflict on this view.
  if (!active && !incomingMatchesStored) {
    return state;
  }

  if (
    state.serviceMode?.messageType === newMode?.messageType &&
    variantMessage(currentInnerMsg?.message) === variantMessage(innerMsg?.message)
  ) {
    return state;
  }

  // Update serviceMap with compound key
  const svcKey = compoundKey(state.bankId, state.liftId);
  const activeName = active ? serviceName : "";
  const updatedServiceMap = { ...state.serviceMap };
  if (activeName) {
    updatedServiceMap[svcKey] = activeName;
  } else {
    delete updatedServiceMap[svcKey];
  }

  return {
    ...state,
    serviceMode: newMode,
    serviceMap: updatedServiceMap,
  };
}

// ---------------------------------------------------------
// Display map accumulation modes
// ---------------------------------------------------------
/**
 * How repeated display values combine: `accumulate` keeps every value seen,
 * `last_wins` keeps only the most recent.
 */
export type DisplayMapMode = "accumulate" | "last_wins";

const usageName = (v: Usage): string => Usage[v];

// Default exclusion groups — backward compatible with previous hardcoded behavior.
// When adapter config provides a OneHotExclusionConfig, use buildExclusionMap() instead.
const DEFAULT_EXCLUSIVE_GROUPS: Map<ExclusionKey, ExclusionRule> = buildDefaultExclusionGroups(
  usageName,
  Usage,
);

// ---------------------------------------------------------
// Derive meta { type, value } from the inner fault message
// ---------------------------------------------------------
function deriveFaultValue(faultMsg: FaultMessageT): string {
  switch (faultMsg.faultType) {
    case FaultContent.ElevatorFaultMessage:
      return Fault[variantMessage((faultMsg.fault as ElevatorFaultMessageT)?.fault)] ?? "";
    case FaultContent.EscalatorFaultMessage:
      return Fault[variantMessage((faultMsg.fault as EscalatorFaultMessageT)?.fault)] ?? "";
    default:
      return "";
  }
}

// ---------------------------------------------------------
// Derive meta { type, value } from the inner service message
// ---------------------------------------------------------
function deriveServiceName(serviceMsg: ServiceMessageT): string {
  switch (serviceMsg.messageType) {
    case DeviceServiceContent.ElevatorServiceMessage:
      return (
        Service[variantMessage((serviceMsg.message as ElevatorServiceMessageT)?.message)] ?? ""
      );
    case DeviceServiceContent.EscalatorServiceMessage:
      return (
        Service[variantMessage((serviceMsg.message as EscalatorServiceMessageT)?.message)] ?? ""
      );
    default:
      return "";
  }
}

// ---------------------------------------------------------
// Bitmask-key label lookups from the union discriminators
// ---------------------------------------------------------
const BITMASK_TYPE_LABEL: Partial<Record<MessageContent, string>> = {
  [MessageContent.Traffic_TrafficMessage]: "Traffic",
  [MessageContent.Control_ControlMessage]: "Control",
  [MessageContent.Fault_FaultMessage]: "Fault",
  [MessageContent.Status_StatusMessage]: "Status",
  [MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage]: "GroupSupervisoryStatus",
  [MessageContent.GroupControl_GroupControlMessage]: "GroupControl",
  [MessageContent.ControllerInfo_ControllerInfoMessage]: "ControllerInfo",
};

const ONEHOT_TYPE_LABEL: Partial<Record<MessageContent, string>> = {
  [MessageContent.Usage_UsageMessage]: "Usage",
  [MessageContent.Service_ServiceMessage]: "Service",
};

/** Convert the union discriminators to the string label used in bitmask keys. */
export function msgTypeLabel(
  contentType: EncodedMessageContent,
  messageType: MessageContent,
): string {
  if (contentType === EncodedMessageContent.BitMaskMessage)
    return BITMASK_TYPE_LABEL[messageType] ?? "";
  if (contentType === EncodedMessageContent.OneHotMessage)
    return ONEHOT_TYPE_LABEL[messageType] ?? "";
  return "";
}

// ---------------------------------------------------------
// Resolve the leaf enum name from the inner union message
// ---------------------------------------------------------
/**
 * Unwraps an encoded message-content union into the concrete value it carries.
 */
export function resolveValue(encoded: {
  contentType: EncodedMessageContent;
  content: BitMaskMessageT | OneHotMessageT | null;
}): string {
  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    const bm = encoded.content as BitMaskMessageT;
    switch (bm.messageType) {
      case MessageContent.Traffic_TrafficMessage: {
        const t = bm.message as TrafficMessageT;
        if (t.messageType === DeviceTrafficMessage.ElevatorTrafficMessage)
          return Traffic[variantMessage((t.message as ElevatorTrafficMessageT).traffic)] ?? "";
        if (t.messageType === DeviceTrafficMessage.EscalatorTrafficMessage)
          return Traffic[variantMessage((t.message as EscalatorTrafficMessageT).traffic)] ?? "";
        break;
      }
      case MessageContent.Control_ControlMessage: {
        const c = bm.message as ControlMessageT;
        if (c.controlType === DeviceControlMessage.ElevatorControlMessage)
          return Control[variantMessage((c.control as ElevatorControlMessageT).control)] ?? "";
        if (c.controlType === DeviceControlMessage.EscalatorControlMessage)
          return Control[variantMessage((c.control as EscalatorControlMessageT).control)] ?? "";
        break;
      }
      case MessageContent.Fault_FaultMessage:
        return deriveFaultValue(bm.message as FaultMessageT);
      case MessageContent.Status_StatusMessage: {
        const conn = extractConnectionStatus(bm.message as StatusMessageT);
        if (conn) return Status[variantMessage(conn.status)] ?? "";
        // Non-connection status — extract the Status enum name directly
        const statusMsg = bm.message as StatusMessageT;
        if (statusMsg.statusType === DeviceStatusContent.ElevatorStatusMessage) {
          const elev = statusMsg.status as ElevatorStatusMessageT | null;
          return elev ? (Status[variantMessage(elev.status)] ?? "") : "";
        }
        if (statusMsg.statusType === DeviceStatusContent.EscalatorStatusMessage) {
          const esc = statusMsg.status as EscalatorStatusMessageT | null;
          return esc ? (Status[variantMessage(esc.status)] ?? "") : "";
        }
        return "";
      }
      default:
        break;
    }
  }

  if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    switch (oh.messageType) {
      case MessageContent.Usage_UsageMessage: {
        const u = oh.message as UsageMessageT;
        if (u.messageType === DeviceUsageContent.ElevatorUsageMessage)
          return Usage[variantMessage((u.message as ElevatorUsageMessageT).usage)] ?? "";
        if (u.messageType === DeviceUsageContent.EscalatorUsageMessage)
          return Usage[variantMessage((u.message as EscalatorUsageMessageT).usage)] ?? "";
        break;
      }
      case MessageContent.Service_ServiceMessage:
        return deriveServiceName(oh.message as ServiceMessageT);
      default:
        break;
    }
  }

  return "";
}

// ---------------------------------------------------------
// Withheld verdicts and marks
// ---------------------------------------------------------

/**
 * The mark a Withheld verdict leaves, or null when the withheld command is not
 * an Elevator Control (only those address a floor a cell can show it on).
 */
function withheldMarkFor(
  innerMessageType: MessageContent,
  innerMessage: unknown,
  atMs: number,
): WithheldMark | null {
  const elevatorControl = elevatorControlOf(innerMessageType, innerMessage);
  return elevatorControl === null ? null : { ...elevatorControl, atMs };
}

/**
 * The Elevator Control state and floor a frame addresses, or null when it
 * carries something else.
 */
function elevatorControlOf(
  innerMessageType: MessageContent,
  innerMessage: unknown,
): { control: Control; position: number } | null {
  if (innerMessageType !== MessageContent.Control_ControlMessage) return null;
  const control = innerMessage as ControlMessageT | null;
  if (control?.controlType !== DeviceControlMessage.ElevatorControlMessage) return null;
  const inner = control.control as ElevatorControlMessageT | null;
  if (!inner) return null;
  const code = variantMessage(inner.control);
  if (code < 0) return null;
  return { control: code as Control, position: inner.position };
}

/**
 * Record where this frame's Elevator Control state came from: another bank,
 * when a bank overlay drew it here (`overlaySourceBankId`), or this lift.
 * Returns the same array when nothing changed.
 */
function updateOverlaid(
  marks: OverlaidMark[],
  fem: FrontEndMessageT,
  innerMessageType: MessageContent,
  innerMessage: unknown,
  active: boolean,
): OverlaidMark[] {
  const elevatorControl = elevatorControlOf(innerMessageType, innerMessage);
  if (elevatorControl === null) return marks;
  const source = fem.overlaySourceBankId === null ? null : bankKey(fem.overlaySourceBankId);
  return updateOverlaidMarks(
    marks,
    elevatorControl.control,
    elevatorControl.position,
    active,
    source,
  );
}

/**
 * Fold an `AckOutcome.Withheld` verdict into state: the verdict and, for an
 * Elevator Control command, a withheld mark on its floor. Nothing else changes,
 * because nothing else happened — VTS did not send the command.
 */
function applyWithheldVerdict(
  state: SocketMessageInterface,
  fem: FrontEndMessageT,
  innerMessageType: MessageContent,
  innerMessage: unknown,
): SocketMessageInterface {
  const nowMs = frameTimeMs(fem);
  const aged =
    nowMs === null ? state.withheldCalls : pruneWithheldMarks(state.withheldCalls, nowMs);
  const mark = withheldMarkFor(innerMessageType, innerMessage, nowMs ?? 0);
  const ack: AckVerdict = {
    outcome: fem.ackOutcome,
    verified: fem.ackVerified,
    elapsedMs: fem.ackElapsedMs,
  };
  // The same verdict delivered twice (a relay retry, a replay) must not stack a
  // second identical mark, or the floor would look withheld twice over.
  const duplicate =
    mark !== null &&
    aged.some(
      (m) => m.position === mark.position && m.control === mark.control && m.atMs === mark.atMs,
    );
  // TP 408 / 413: the verdict always produces a new state (its ack differs).
  tracePointEmit(TracePointId.FrontendDeltaApplied);
  tracePointEmit(TracePointId.FrontendContextMerged);
  return { ...state, ack, withheldCalls: mark && !duplicate ? [...aged, mark] : aged };
}

/**
 * The floor a car call just registered on, or null. Either front report counts:
 * `CarCall` and `CarCallAnswered` both mean the call is standing at `position`.
 */
function carCallRegisteredAt(
  innerMessageType: MessageContent,
  innerMessage: unknown,
  active: boolean,
): number | null {
  if (!active || innerMessageType !== MessageContent.Traffic_TrafficMessage) return null;
  const traffic = innerMessage as TrafficMessageT | null;
  if (traffic?.messageType !== DeviceTrafficMessage.ElevatorTrafficMessage) return null;
  const inner = traffic.message as ElevatorTrafficMessageT | null;
  if (!inner) return null;
  const code = variantMessage(inner.traffic);
  return code === Traffic.CarCall || code === Traffic.CarCallAnswered ? inner.position : null;
}

/**
 * Age the lift's withheld marks against this frame and clear the floor a car
 * call just registered on. Returns the same array when nothing changed.
 */
function updateWithheldMarks(
  marks: WithheldMark[],
  fem: FrontEndMessageT,
  innerMessageType: MessageContent,
  innerMessage: unknown,
  active: boolean,
): WithheldMark[] {
  if (marks.length === 0) return marks;
  const nowMs = frameTimeMs(fem);
  const aged = nowMs === null ? marks : pruneWithheldMarks(marks, nowMs);
  const position = carCallRegisteredAt(innerMessageType, innerMessage, active);
  return position === null ? aged : clearWithheldMarksAt(aged, position);
}

// ---------------------------------------------------------
// MAIN: Apply a FrontEndMessageT to current state
// ---------------------------------------------------------
/**
 * Merges a monitoring delta frame into the current lift state.
 *
 * Deltas arrive at 5 Hz and carry only changed fields, so this must merge rather
 * than replace: a frame that omits a field means "unchanged", not "cleared".
 *
 * A sequence progress report is returned unapplied: it carries the commanded
 * message and is not lift state. MonitoringWebSocket diverts reports before
 * they get here; this keeps any other caller from applying one.
 */
export function applyDeltaToState(
  currentState: SocketMessageInterface,
  fem: FrontEndMessageT,
  displayMapMode?: DisplayMapMode,
  exclusionGroups?: Map<ExclusionKey, ExclusionRule>,
  hallCallBindings?: ReadonlyArray<HallCallBinding>,
): SocketMessageInterface {
  if (isSequenceProgress(fem)) return currentState;
  const active = fem.msg!.active;
  const encoded = fem.msg!.msg!;
  let newState: SocketMessageInterface;
  let isService = false;

  // Extract the inner MessageContent regardless of wrapper (BitMask or OneHot).
  // Both wrappers carry the same union — this collapses the two paths so every
  // message type is handled under either wrapper.
  let innerMessageType: MessageContent;
  let innerMessage: unknown;

  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    const bm = encoded.content as BitMaskMessageT;
    innerMessageType = bm.messageType;
    innerMessage = bm.message;
  } else if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    innerMessageType = oh.messageType;
    innerMessage = oh.message;
  } else {
    newState = currentState;
    innerMessageType = MessageContent.NONE;
    innerMessage = null;
  }

  // A Withheld verdict carries the command VTS refused to send. Applying it
  // would record as done the one thing that did not happen (a SecureCarCall
  // becoming active, a lock icon flipping), so it records the verdict and a
  // mark and nothing else — no device delta, no bitmask, no last-message.
  if (fem.ackOutcome === AckOutcome.Withheld) {
    return applyWithheldVerdict(currentState, fem, innerMessageType, innerMessage);
  }

  switch (innerMessageType) {
    case MessageContent.Traffic_TrafficMessage: {
      const traffic = innerMessage as TrafficMessageT;
      switch (traffic.messageType) {
        case DeviceTrafficMessage.ElevatorTrafficMessage: {
          const inner = traffic.message as ElevatorTrafficMessageT;
          newState = applyElevatorTrafficDelta(currentState, inner, active);
          break;
        }
        case DeviceTrafficMessage.EscalatorTrafficMessage: {
          newState = currentState; // escalator traffic is bitmask-only
          break;
        }
        default:
          newState = currentState;
      }
      break;
    }
    case MessageContent.Control_ControlMessage: {
      const control = innerMessage as ControlMessageT;
      switch (control.controlType) {
        case DeviceControlMessage.ElevatorControlMessage: {
          const inner = control.control as ElevatorControlMessageT;
          newState = applyElevatorControlDelta(currentState, inner, active);
          // A Control message the switch above has no arm for still reaches
          // here, which is the point: EnergizeUpHallCall carries the floor and
          // was previously discarded.
          newState = applyHallCallBindings(
            newState,
            encoded.contentType,
            innerMessageType,
            variantMessage(inner.control),
            inner.position,
            active,
            hallCallBindings,
          );
          break;
        }
        case DeviceControlMessage.EscalatorControlMessage: {
          const inner = control.control as EscalatorControlMessageT;
          newState = applyEscalatorControlDelta(currentState, inner, active);
          break;
        }
        default:
          newState = currentState;
      }
      break;
    }
    case MessageContent.Fault_FaultMessage: {
      const faultMsg = innerMessage as FaultMessageT;
      newState = applyFaultDelta(currentState, faultMsg, active);
      break;
    }
    case MessageContent.Status_StatusMessage: {
      const statusMsg = innerMessage as StatusMessageT;
      const connMsg = extractConnectionStatus(statusMsg);
      // Only Connected and Disconnected are connectivity statements. Heartbeat
      // is positive liveness evidence on an ALTERNATING bit, so half its frames
      // carry active:false -- reading it as "not Connected, therefore offline"
      // latched real lifts offline and blanked the bank's hall-call strip.
      const verdict = connMsg
        ? connectivityFromStatus(variantMessage(connMsg.status), active, {
            connected: Status.Connected,
            disconnected: Status.Disconnected,
          })
        : null;
      newState =
        verdict === null || currentState.connected === verdict
          ? currentState
          : { ...currentState, connected: verdict };
      break;
    }
    case MessageContent.Usage_UsageMessage: {
      const usage = innerMessage as UsageMessageT;
      switch (usage.messageType) {
        case DeviceUsageContent.ElevatorUsageMessage: {
          const inner = usage.message as ElevatorUsageMessageT;
          newState = applyElevatorUsageDelta(currentState, inner, active);
          break;
        }
        case DeviceUsageContent.EscalatorUsageMessage: {
          const inner = usage.message as EscalatorUsageMessageT;
          newState = applyEscalatorUsageDelta(currentState, inner, active);
          break;
        }
        default:
          newState = currentState;
      }
      break;
    }
    case MessageContent.Service_ServiceMessage: {
      isService = true;
      const serviceMsg = innerMessage as ServiceMessageT;
      const serviceName = deriveServiceName(serviceMsg);
      newState = applyServiceDelta(currentState, serviceMsg, active, serviceName);
      break;
    }
    // GSS, GC, CI — pass through for bitmask only (no state mutation)
    case MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage:
    case MessageContent.GroupControl_GroupControlMessage:
    case MessageContent.ControllerInfo_ControllerInfoMessage:
      newState = currentState;
      break;
    default:
      newState = currentState;
      break;
  }

  // Read the union discriminators directly
  const contentType = encoded.contentType;
  const content = encoded.content as BitMaskMessageT | OneHotMessageT | null;
  const messageType = innerMessageType;

  // Update msg if the discriminators or active flag changed
  const msgChanged =
    newState.msg.contentType !== contentType ||
    newState.msg.messageType !== messageType ||
    newState.msg.content !== content ||
    newState.msg.active !== active;

  const updatedMsg = msgChanged ? { contentType, messageType, content, active } : newState.msg;

  // Update oneHotMap if the message is a OneHotMessage
  let updatedOneHotMap = newState.oneHotMap;
  if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    const ohk = oneHotKey(newState.bankId, newState.liftId, oh.messageType);
    const entry: OneHotEntry = { active, messageType: oh.messageType };
    if (
      updatedOneHotMap[ohk]?.active !== active ||
      updatedOneHotMap[ohk]?.messageType !== oh.messageType
    ) {
      updatedOneHotMap = { ...newState.oneHotMap, [ohk]: entry };
    }
  }

  // Only update bitmask if a new entry differs from existing.
  // Skip service deltas — service info is stored in serviceMap only.
  let updatedBitmask = newState.bitmask;
  const typeLabel = msgTypeLabel(contentType, messageType);
  const value = resolveValue(encoded);
  if (value && !isService) {
    const suffix = `${typeLabel}:${value}`;
    const prefix = bitmaskPrefix(newState.bankId, newState.liftId);
    const compoundBitmaskKey = `${prefix}${suffix}`;
    if (newState.bitmask[compoundBitmaskKey] !== active) {
      updatedBitmask = {
        ...newState.bitmask,
        [compoundBitmaskKey]: active,
      };

      // In "last_wins" mode, clear mutually exclusive peers — gated by the
      // per-group `evictOnlyWhenActive` flag.  When that flag is true
      // (schema default), an incoming `active=false` does NOT touch any
      // peer; only an active incoming evicts.  When the flag is false,
      // any incoming value evicts other configured values.
      if (displayMapMode === "last_wins") {
        const groups = exclusionGroups ?? DEFAULT_EXCLUSIVE_GROUPS;
        const rule = groups.get(suffix);
        if (rule && (active || !rule.evictOnlyWhenActive)) {
          rule.peers.forEach((peer) => {
            updatedBitmask[`${prefix}${peer}`] = false;
          });
        }
      }
    }
  }

  // The acknowledgement verdict this frame reports, if any.
  //
  // Read straight off the envelope rather than from a delta, because it is not
  // device state: it is what VTS's correlator concluded about a command, and
  // the message carrying it is the command that was answered.
  //
  // It participates in the unchanged-state check below for a specific reason. A
  // verdict frame repeats the message that was COMMANDED, so every delta it
  // produces is one the state already has and `msgChanged` is false. Without
  // this, the one frame that reports whether a command worked would be
  // discarded as "nothing changed" -- the exact opposite of what it exists for.
  const ack: AckVerdict =
    fem.ackOutcome === AckOutcome.None &&
    fem.ackVerified === AckVerified.Unknown &&
    fem.ackElapsedMs === 0
      ? NO_ACK_VERDICT
      : {
          outcome: fem.ackOutcome,
          verified: fem.ackVerified,
          elapsedMs: fem.ackElapsedMs,
        };
  const ackChanged = ack !== newState.ack;

  // Withheld marks age against this frame's clock, and a floor's mark clears
  // once a car call registers there — the held-back press has been superseded.
  const withheldCalls = updateWithheldMarks(
    newState.withheldCalls,
    fem,
    innerMessageType,
    innerMessage,
    active,
  );
  const withheldChanged = withheldCalls !== newState.withheldCalls;

  // A bank overlay's copy names the bank it came from; the lift's own report of
  // the same state takes the mark away.
  const overlaid = updateOverlaid(newState.overlaid, fem, innerMessageType, innerMessage, active);
  const overlaidChanged = overlaid !== newState.overlaid;

  // Nothing at the frame level changed, so no state object is built here.
  //
  // This returns `newState`, which is `currentState` itself only when the
  // delta helper above also left the state alone. Several do not: the
  // car-call, hall-call, lock, door and elevator direction paths build a new
  // object even when what they record is already there, so a state deep-equal
  // to `currentState` can come back as a different object. Identity is
  // therefore not a "this frame changed something" signal; compare values.
  //
  // A frame decoded from the wire does not reach this branch at all: each one
  // is unpacked into objects of its own, so its `content` is never the one the
  // state recorded and `msgChanged` is true. Only re-applying the same decoded
  // object, or a frame that carries no content, can get here.
  if (
    !msgChanged &&
    !ackChanged &&
    !withheldChanged &&
    !overlaidChanged &&
    updatedBitmask === newState.bitmask &&
    updatedOneHotMap === newState.oneHotMap
  ) {
    // TP 408 alone: FrontendDeltaApplied — delta processed, no frame-level
    // change. It does not mean the returned reference is `currentState`.
    tracePointEmit(TracePointId.FrontendDeltaApplied);
    return newState;
  }

  // TP 408: FrontendDeltaApplied — delta processed and applied to state
  tracePointEmit(TracePointId.FrontendDeltaApplied);
  // TP 413: FrontendContextMerged — frame-level fields merged into a new state object
  tracePointEmit(TracePointId.FrontendContextMerged);

  return {
    ...newState,
    msg: updatedMsg,
    bitmask: updatedBitmask,
    oneHotMap: updatedOneHotMap,
    ack,
    withheldCalls,
    overlaid,
  };
}
